
import {forkJoin as observableForkJoin, of as observableOf, throwError as observableThrowError, from as observableFrom, interval as observableInterval,  Observable ,  BehaviorSubject } from 'rxjs';

import {map, mergeMap, debounce, distinctUntilChanged, catchError} from 'rxjs/operators';
import jsonLogic from 'json-logic-js';
import { Injectable } from '@angular/core';
import { ApiService } from '../api-service/api.service';
import { INode, NodeType } from '../Configuration/INode';
import { Node } from '../Configuration/Node';
import { NodeSort } from '../Configuration/node.sort';
import { Attribute } from '../attribute/Attribute';
import { IAttribute, AttributeType } from '../attribute/IAttribute';
import { LoggerService } from '../logger-service/logger.service';
import { IndexedDBService } from '../indexed-db-service/indexed-db.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';







import { HttpClient } from '@angular/common/http';

@Injectable()
export class OxeModelService {
    errorDetailTarget: any;

    private _rawStructure: Object;
    private _loaderSubject: BehaviorSubject<boolean>;
    loaderObserver: Observable<boolean>;
    private _selectedNodeSubject: BehaviorSubject<INode>;
    selectedNodeObserver: Observable<INode>;
    private _helpNodeSubject: BehaviorSubject<INode>;
    helpNodeObserver: Observable<INode>;
    private _rootNode: INode;
    private classStructureCache: Object = {};
    public errorMsg = "";
    public errorCode = "";
    public errorDetailMsg = "";

    constructor(private _apiService: ApiService, private _logger: LoggerService, private _translate: TranslateService, private _indexedDB: IndexedDBService,private _authhttp: HttpClient) {
        this._loaderSubject = new BehaviorSubject(true);
        this._selectedNodeSubject = new BehaviorSubject(null);
        this._helpNodeSubject = new BehaviorSubject(null);

        this.loaderObserver = this._loaderSubject.asObservable().pipe(
            distinctUntilChanged(),
            debounce(() => observableInterval(300)),);

        this.selectedNodeObserver = this._selectedNodeSubject.asObservable();
        this.helpNodeObserver = this._helpNodeSubject.asObservable();

        jsonLogic.add_operation('Contains', (a, b) => {
            if (typeof a.indexOf === 'undefined') {
                return false;
            }
            return (a.indexOf(b) !== -1);
        });
    }

    public getOperatorsForAttributeType(attributeType: string): Array<string> {
        switch (attributeType) {
            case 'Boolean':
                return ['equals', 'different'];
            case 'Enumerated':
                return ['equals', 'different'];
            case 'Integer':
                return ['equals', 'different', 'greater-or-equal', 'less-or-equal'];
            case 'Byte_String':
            case 'Octet_String':
                return ['equals', 'different', 'begins-with', 'ending-with', 'empty', 'not-empty'];
            // AttributeType.SetOf and AttributeType.Sequence cannot be filtered
            default:
                return [];
        }
    }

    public getJSONLogicOperatorsForAttributeType(attributeType: string): Array<string> {
        switch (attributeType) {
            case 'Boolean':
                return ['==', '!='];
            case 'Enumerated':
                return ['==', '!='];
            case 'Integer':
                return ['==', '!=', '>', '<', '>=', '<='];
            case 'Byte_String':
            case 'Octet_String':
                return ['==', '!=', 'Contains'];
            case 'Sequence':
            case 'Set_Of':
                return ['Contains'];
            default:
                return [];
        }
    }

    public retrieveOxeModel(option?: string): Observable<INode> {
    this._loaderSubject.next(true);

    return this._apiService.retrieveModelStructure(option).pipe(
        mergeMap(structure => observableFrom($RefParser.dereference(structure))),
        map(derefStructure => this._rawStructure = derefStructure),
        mergeMap(this.createRootClassNode.bind(this)),
        // Explicitly type the result as INode
        map((result): INode => result as INode),
        catchError((error: any) => {
            this._loaderSubject.next(false);
            return observableThrowError(error);
        })
    );
}

    private createRootClassNode(): Observable<INode> {
        this._rootNode = new Node(NodeType.Class, undefined, this._rawStructure['Node']);
        this.helpNode(this._rootNode);
        return this.retrieveSubNodes(this._rootNode).pipe(
            map(subNodes => subNodes[0]));
    }

    public getVersion(): Observable<Object> {
        return this._apiService.retrieveVersion().pipe(
            map(response => response["Version_Name"]),
            catchError(e => {
                return observableOf({});
            })
        );
    }

    public retrieveSubNodes(node: INode, fromLocate = false): Observable<Array<INode>> {
        if (node.isObjectNode) {
            let obs = Observable.create(observer => {
                if (!node.subNodes) {
                    let nodes: Array<INode> = [];
                    let nodeStructure = node['structure'];
                    // must have a structure
                    if (nodeStructure && nodeStructure.hasOwnProperty('subClasses')) {
                        for (let key in nodeStructure['subClasses']) {
                            if (nodeStructure['subClasses'].hasOwnProperty(key)) {
                                let element = nodeStructure['subClasses'][key];
                                let subNode: INode = new Node(NodeType.Class, node, element);
                                nodes.push(subNode);
                            }
                        }
                    }
                    node.subNodes = nodes;
                    node.evaluateSubNodesUsedWhen();
                }
                observer.next(node.subNodes);
                observer.complete();
            });
            return obs;
        } else {
            return this.retrieveData(node, true, fromLocate) as Observable<Array<INode>>;
        }
    }

    public retrieveHelp(lang: string): Observable<Object> {
        return this._apiService.retrieveHelp(lang).pipe(
            map(response => this.parseHelpBody(response)));
    }

    private parseHelpBody(response): Object {
        const helpData = {};
        const keysWithMultipleValues = {};

        const body: string = response;
        const bodyLength = body.length;

        const helpPattern = '_help"';
        const helpPatternLength = helpPattern.length;

        this._logger.debug('OxeModelService', 'parseHelpBody: before parsing body...');

        //
        // Parse body to find all keys / values, and detect keys which are present several times
        //
        let currentIndex = 0;

        do {
            //look for key
            const keyFirstQuoteIndex = body.indexOf('"', currentIndex);
            if (keyFirstQuoteIndex === -1) {
                break;
            }
            const keyHelpPatternIndex = body.indexOf(helpPattern, keyFirstQuoteIndex);
            if (keyHelpPatternIndex === -1) {
                return response.json();
            }
            let key = body.substring(keyFirstQuoteIndex + 1, keyHelpPatternIndex);

            //look for value
            const valueFirstQuoteIndex = body.indexOf('"', keyHelpPatternIndex + helpPatternLength);
            const valueLastQuoteIndex = body.indexOf('"', valueFirstQuoteIndex + 1);

            const value = body.substring(valueFirstQuoteIndex + 1, valueLastQuoteIndex).replace(/\\n/g, '\n');

            //check for already existing key
            if (helpData[key] !== undefined) {
                //the key already exists : we store all the values for this key in a array into the keysWithMultipleValues map
                const existingEntryWithMultipleValues = keysWithMultipleValues[key];
                if (existingEntryWithMultipleValues === undefined) {
                    //create entry in keysWithMultipleValue with the existing value in helpData and the new parsed value
                    keysWithMultipleValues[key] = [helpData[key], value];
                } else {
                    //add new parsed value to existing multiple values array for this key
                    existingEntryWithMultipleValues.push(value);
                }
            } else {
                helpData[key] = value;
            }

            currentIndex = valueLastQuoteIndex + 1;
        } while (currentIndex < bodyLength);

        //this._logger.debug('OxeModelService', 'parseHelpBody: after parsing body: keys in helpData:' + Object.keys(helpData).length + ' nb keys with multiple values:' + Object.keys(keysWithMultipleValues).length);

        //clear CacheStructure cache
        this.classStructureCache = {};

        //
        // For all keys with multiple values, add an entry in helpData map with a key being an unique attribute path
        //
        Object.keys(keysWithMultipleValues).forEach((key) => {
            const multipleValues = keysWithMultipleValues[key];

            const keySlashIndex = key.indexOf('/');
            if (keySlashIndex !== -1) {
                const classname = key.substring(0, keySlashIndex);
                const attrname = key.substring(keySlashIndex + 1);

                const attributePathNames: string[] = this.findAttributePathsByClassAndAttributeName(classname, attrname);
                if (attributePathNames && attributePathNames.length === multipleValues.length) {
                    //Replace each occurence by its corresponding path
                    delete helpData[key];
                    attributePathNames.forEach((path: string, index: number) => {
                        helpData[path] = multipleValues[index];
                    });
                }
            }
        });

        this._logger.debug('OxeModelService', 'parseHelpBody: after multiple values treatment => keys in helpData:' + Object.keys(helpData).length);

        //remove empty entries
        Object.keys(helpData).forEach((key) => {
            if (helpData[key] === undefined || helpData[key] === '') {
                delete helpData[key];
            }
        });

        this._logger.debug('OxeModelService', 'parseHelpBody: after clean empty keys => keys in helpData:' + Object.keys(helpData).length);

        return helpData;
    }

    private findClassStructure(searchedClassname: string): Object {
        //First look in classStructure cache
        let classStructure = this.classStructureCache[searchedClassname];
        if (classStructure) {
            return classStructure;
        }

        //search class Structure in model raw startucture
        const rootNodeStructure = this._rawStructure['Node'];
        if (searchedClassname === 'Node') {
            classStructure = rootNodeStructure;
        } else if (rootNodeStructure.hasOwnProperty('subClasses')) {
            classStructure = this.findClassStructureInClassList(searchedClassname, rootNodeStructure['subClasses']);
        }
        if (classStructure) {
            //insert in classStructure cache
            this.classStructureCache[searchedClassname] = classStructure;
        }
        return classStructure;
    }

    private findClassStructureInClassList(searchedClassname: string, classStructList: Object): Object {
        if (classStructList === undefined) {
            return undefined;
        }
        //First search the class name in the class list
        const found = Object.keys(classStructList).indexOf(searchedClassname);
        if (found >= 0) {
            return classStructList[searchedClassname];
        } else {
            //Else search the class name in the subClasses
            let searchClassStruct;
            Object.keys(classStructList).some((className) => {
                if (classStructList[className].hasOwnProperty('subClasses')) {
                    searchClassStruct = this.findClassStructureInClassList(searchedClassname, classStructList[className]['subClasses']);
                    return searchClassStruct !== undefined;
                }
                return false;
            });
            return searchClassStruct;
        }
    }

    private findAttributePathsByClassAndAttributeName(className: string, attributeName: string): string[] {
        const classStructure = this.findClassStructure(className);
        if (classStructure && classStructure.hasOwnProperty('attributes')) {
            return this.findAttributePathsByAttributeName(attributeName, classStructure['attributes'], className);
        }
        return undefined;
    }

    /*
    * Search all occurences of attribute name in class attributes list (and sequence sub attributes)
    */
    private findAttributePathsByAttributeName(searchedAttributeName: string, attrStructList: Object, path: string): string[] {
        let res = [];
        if (searchedAttributeName === undefined || attrStructList === undefined) {
            return [];
        }

        //First search for the attribute name in class attributes list
        const found = Object.keys(attrStructList).indexOf(searchedAttributeName);
        if (found >= 0) {
            res.push(path + '/' + searchedAttributeName);
        }

        //Then search for the attribute name in the sequence sub elements of attributes, or in a digit of a byte_string
        Object.keys(attrStructList).forEach((subAttributeName) => {
            const attrStruct = attrStructList[subAttributeName];
            if (attrStruct.hasOwnProperty('type') && attrStruct['type'] && attrStruct['type'].hasOwnProperty('name')) {
                const attrTypeName = attrStruct['type']['name'];
                if (attrTypeName && attrTypeName === 'Sequence') {
                    //The attribute is of type 'Sequence'
                    res.push.apply(res, this.findAttributePathsByAttributeName(searchedAttributeName, attrStruct['type']['sequence'], path + '/' + subAttributeName));
                } else if (attrTypeName && attrTypeName === 'Set_Of') {
                    const setofSubTypeName = attrStruct['type']['set_of'] ? attrStruct['type']['set_of']['name'] : undefined;
                    if (setofSubTypeName && setofSubTypeName === 'Sequence') {
                        //The attribute is of type 'Set_Of' with subtype 'Sequence'
                        res.push.apply(res, this.findAttributePathsByAttributeName(searchedAttributeName, attrStruct['type']['set_of']['sequence'], path + '/' + subAttributeName));
                    }
                } else if (attrTypeName && attrTypeName === 'Byte_String') {
                    //add help for byte string digits by adding a path to
                    const attrTypeDigits: Array<Object> = attrStruct['type']['digits'];
                    if (attrTypeDigits) {
                        attrTypeDigits.some((digit) => {
                            if (digit === searchedAttributeName) {
                                res.push(path + '/' + subAttributeName + '/' + searchedAttributeName);
                                return true;
                            }
                            return false;
                        });
                    }
                }
            }
        });

        return res;
    }

    public findAndUpdateObjectNode(parentNode: INode, fromObject: Object, index: number, isLocal?: boolean): INode {
        if (!parentNode.subNodes) {
            parentNode.subNodes = [];
        }

        ///
        /// look for an already existing node name
        ///
        let newNode = new Node(NodeType.Object, parentNode, parentNode['structure'], fromObject, index, isLocal);
        //ignore new server node with undefined name (eg undefined id)
        if (!newNode.name && !isLocal) {
            return undefined;
        }
        let existingNode: Node = parentNode.subNodes.find((x: INode) => { return x.name === newNode.name && x.isLocal === false; }) as Node;

        if (!existingNode || isLocal) {
            existingNode = newNode;
            existingNode.isLocal = !!isLocal;
            if (isLocal === true) {
                existingNode.localIdx = Node.nextLocalIdx++;
            }

            if (existingNode.isOneInstance) {
                this.retrieveSubNodes(existingNode)
                    .subscribe();
            }

            if (isLocal) {
                //insert local node before server nodes (if any), at end of local nodes
                let firstServerNodeIndex: number = parentNode.subNodes.findIndex(node => node.isLocal === false);
                if (firstServerNodeIndex !== -1) {
                    //we found one server node: let insert the new local node before this server node
                    parentNode.subNodes.splice(firstServerNodeIndex, 0, existingNode);
                } else {
                    //no server node found: we add the new local node at end of array
                    parentNode.subNodes.push(existingNode);
                }
            } else {
                //add at end of array
                parentNode.subNodes.push(existingNode);
            }
            parentNode.subNodesInitialLength++;
        } else {
            existingNode.index = index;
            existingNode.updateData(fromObject);
        }

        return existingNode;
    }

    public resolveAttributeString(attr: IAttribute): Observable<string> {
        if (attr.isSimpleType) {
            let attrValue = attr.localValue !== undefined ? attr.localValue : attr.value;
            if (attrValue !== undefined && attrValue !== null) {
                if (attr.isEnumerated) {
                    return this._translate.get(attrValue.toString());
                } else if (attr.isByteString) {
                    return observableOf(Object.keys(attrValue).map(key => attrValue[key]).join(''));
                } else {
                    return observableOf(attrValue.toString());
                }
            }
        } else {
            if (attr.isSequence || attr.isSetOf) {
                let attrValue = attr.value; // do not take localValue !
                if (attrValue.length > 0) {
                    let obsArray = attrValue.map(subattr => this.resolveAttributeString(subattr));
                    let obsMulti = observableForkJoin(obsArray).pipe(map(labels => '<' + labels.join(',') + '>'));
                    return obsMulti;
                }
            }
        }
        return observableOf('');
    }

    public retrieveData(node: INode, shouldUseQuery = true, fromLocate = false): Observable<Object> {
        this._loaderSubject.next(true);

        let url = node.getPath();
        let query: string = undefined;
        let filterUrl: Boolean = true;
        if (node.isClassNode) {
            if (!node.isGettable) {
                if(!fromLocate)
                this._loaderSubject.next(false);
                // Create a fake one_instance subNode
                if (!node.subNodes) {
                    node.subNodes = [];
                    this.findAndUpdateObjectNode(node, { One_Instance: 1 }, 0);
                }
                if(!fromLocate)
                this._loaderSubject.next(false);
                this.initAndFilterDisplayedSubNodes(node);
                return observableOf(node.subNodes);
            }

            if (node.isOneInstance) {
                url += '/1';
            } else {

                //add attributes to retrieve to the url, but exclude the attribute which is the class id
                const retrievedAttributes = node.getInitialAttributesToRetrieve().filter(attr => attr.name !== node.structure['id']);
                let solefilteringAttributeKey = node.getSoleFilteringAttributeKey();
                let isIdAttribute = false;

                if (!!solefilteringAttributeKey) {
                    node.attributes.map(attr => {
                        if (attr.name === node.currentFilters[solefilteringAttributeKey]['name']) {
                            isIdAttribute = attr.isId
                        }
                    })
                }
                //In case of the search is done on single attribute and the filtering is not set for the attribute
                if(!!solefilteringAttributeKey && shouldUseQuery)
                    if (isIdAttribute && (!node.attributes[0].isFiltering) && (node.currentFilters[solefilteringAttributeKey]['operator'] === '==')) {
                        url += '/' + node.currentFilters[solefilteringAttributeKey]['value'];
                        filterUrl = false;
                    }
                if (retrievedAttributes.length > 0) {
                    url += '?attributes=' + retrievedAttributes.map(attr => attr.name).join(',');
                }

                // "Filtering a collection of objects", a couple rules
                // 1. It is only applicable if there is only one active filter
                // 2. For attribute type Octet_String:
                //      a. The equal operator on attribute ID is supported
                //      b. if the value is empty, the not equal (!=) operator is also supported
                // 3. For attribute type enumerated, and boolean, all operators are supported (i.e. 'equals and 'not-equals')
                // 4. For attribute type Integer, the following operators are supported: ['==', '!=', '>=', '<='];

                if (filterUrl && (!!solefilteringAttributeKey && shouldUseQuery)) {
                    switch (node.getAttributeByName(node.currentFilters[solefilteringAttributeKey]['name']).type) {
                        case AttributeType.Octet_String:
                            if (node.structure['id'] === node.currentFilters[solefilteringAttributeKey]['name']) {
                                if (node.currentFilters[solefilteringAttributeKey]['operator'] === '==') {
                                    query = '&Query['
                                        + node.currentFilters[solefilteringAttributeKey]['name'] + '][=]='
                                        + node.currentFilters[solefilteringAttributeKey]['value'];
                                } else if (node.currentFilters[solefilteringAttributeKey]['operator'] === '!='
                                    && node.currentFilters[solefilteringAttributeKey]['value'] === '') {
                                    query = '&Query['
                                        + node.currentFilters[solefilteringAttributeKey]['name'] + '][!=]='
                                        + node.currentFilters[solefilteringAttributeKey]['value'];
                                }
                            }
                            break;
                        case AttributeType.Enumerated:
                        case AttributeType.Boolean:
                            if (node.currentFilters[solefilteringAttributeKey]['operator'] === '==') {
                                query = '&Query['
                                    + node.currentFilters[solefilteringAttributeKey]['name'] + '][=]='
                                    + node.currentFilters[solefilteringAttributeKey]['value'];
                            } else {
                                query = '&Query['
                                    + node.currentFilters[solefilteringAttributeKey]['name'] + ']['
                                    + node.currentFilters[solefilteringAttributeKey]['operator']
                                    + ']='
                                    + node.currentFilters[solefilteringAttributeKey]['value'];
                            }
                            break;
                        case AttributeType.Integer:
                            if (node.currentFilters[solefilteringAttributeKey]['operator'] === '==') {
                                query = '&Query['
                                    + node.currentFilters[solefilteringAttributeKey]['name'] + '][=]='
                                    + node.currentFilters[solefilteringAttributeKey]['value'];
                            } else if (node.currentFilters[solefilteringAttributeKey]['operator'] === '=='
                                || node.currentFilters[solefilteringAttributeKey]['operator'] === '!='
                                || node.currentFilters[solefilteringAttributeKey]['operator'] === '>='
                                || node.currentFilters[solefilteringAttributeKey]['operator'] === '<=') {
                                query = '&Query['
                                    + node.currentFilters[solefilteringAttributeKey]['name'] + ']['
                                    + node.currentFilters[solefilteringAttributeKey]['operator']
                                    + ']='
                                    + node.currentFilters[solefilteringAttributeKey]['value'];
                            }
                            break;
                        default:
                            break;
                    }
                }
            }
        }

        if (!!query) {
            url += query;
        }

    return this._apiService.retrieveObject(url, false).pipe( // ✅ Added .pipe()

    map(object => {
        if (!fromLocate)
            this._loaderSubject.next(false);

        if (node.isObjectNode) {
            node.updateAndCreateMissingData(object);
            return node;
        } else {
            if (!node.subNodes) {
                node.subNodes = [];
            } else {
                this.removeUnchangedSubNodes(node);
            }

            if (Array.isArray(object)) {
                let serverObjects = object as Array<Object>;
                serverObjects.forEach((obj: Object, index: number) => {
                    this.findAndUpdateObjectNode(node, obj, index);
                });

                this.reOrderSubNodes(node);
            } else {
                this.findAndUpdateObjectNode(node, object, 0);
            }

            node.subNodesInitialLength = node.subNodes.length;
            this.filterAndSortDisplayedSubNode(node);

            return node.subNodes;
        }
    }),

    // ✅ Replaced deprecated `.catch(...)` with `.pipe(catchError(...))`
    catchError((error: any) => {
        if (!filterUrl) {
            if (!fromLocate)
                this._loaderSubject.next(false);

            node.subNodes = [];
            node.isExpanded = true;
            node.filterApplied();

            return observableOf(node.subNodes); // ✅ Wrapped in observableOf
        } else if (query && shouldUseQuery) {
            return this.retrieveData(node, false); // ✅ No change needed
        } else {
            if (!fromLocate)
                this._loaderSubject.next(false);

            this.handleError(node, error);

            return observableThrowError(error); // ✅ Updated throw to observableThrowError
        }
    })
);
    }

    public isProfiledUser(node): boolean {
        if (node.parent.name === "Subscriber") {
            let attr = node.getAttributeByName("Set_Role");
            if (attr === undefined) { return false; }
            else {
                let a = node.getAttributeData(attr);
                if (a === "Profile_Role")
                    return true;
                return false;
            }
        }
        else
            return false;

    }

    public removeUnchangedSubNodes(node: INode, keepSelected = true) {
        //Caution: we must keep the local subnodes, the modified subnodes,
        //and also any subnode in selected node's path if keepSelected is true
        if (node.subNodes) {
            node.subNodes = node.subNodes.filter(subnode => {
                const selectedNode = this._selectedNodeSubject.getValue();
                const subnodeIsSelectedOrIsInTheSelectedNodePath = selectedNode && ((selectedNode === subnode) || selectedNode.getPath().startsWith(subnode.getPath().concat('/')));
                if (!keepSelected && subnodeIsSelectedOrIsInTheSelectedNodePath) {
                    // if the subnode is in the selected node's path and we don't want to keep the selected node, we must change the selection to its parent
                    this.selectNode(node);
                }
                return ((keepSelected && subnodeIsSelectedOrIsInTheSelectedNodePath) ||
                    subnode.isLocal || subnode.isModified);
            });
        }
    }

    private initAndFilterDisplayedSubNodes(node: INode): void {
        node.initDisplayedSubNodes();
        this.filterDisplayedSubNodes(node);
    }

    private filterDisplayedSubNodes(node: INode) {
        if (!!node.filterCondition && node.subNodes) {
            //do filter
            node.subNodes = node.subNodes.filter((subnode: INode) => {
                if (subnode.isLocal || subnode.isModified) {
                    // local nodes and modified nodes are not filtered
                    return true;
                }
                let data = {};
                subnode.attributes.forEach((attr: IAttribute) => {
                    // apply filter on attribute that is 'used' in this node
                    if (attr.isUsed) {
                        if (attr.isOctetString) {
                            // stringify and remove accents from string and convert to lowercase for filtering comparisons
                            data[attr.name] = Attribute.removeDiacritics(this.getStringValueOf(attr)).toLowerCase();
                        } else {
                            data[attr.name] = this.getStringValueOf(attr);
                        }
                    }
                });
                return subnode.attributes && jsonLogic.apply(node.filterCondition, data);
            }) as Array<INode>;
        }
    }

    public getStringValueOf(attr: IAttribute): string {
        let result = '';
        if (attr.isSimpleType) {
            let attrValue = attr.localValue !== undefined ? attr.localValue : attr.value;
            if (attrValue) {
                if (attr.isOctetString) {
                    result = attrValue;
                } else if (attr.isByteString) {
                    result = Object.keys(attrValue).map(key => attrValue[key]).join('');
                } else {
                    result = attrValue.toString();
                }
            } else if(attr.isBoolean) {
                result = attrValue.toString();
            }
        } else {
            if (attr.isSequence || attr.isSetOf) {
                if (attr.value.length > 0) {
                    result = attr.value.map(subattr => this.getStringValueOf(subattr)).join(' ');
                }
            }
        }
        return result;
    }

    /**
     * Method re-order subNodes of parentNode based on the server order (index) stored in node (and sort local nodes by local index)
     *
     * @param {INode} parentNode
     *
     * @memberOf OxeModelService
     */
    private reOrderSubNodes(parentNode: INode) {
        if (parentNode.subNodes) {
            parentNode.subNodes.sort((a, b) => {
                const a_index = (a.index === undefined) ? -1 : a.index;
                const b_index = (b.index === undefined) ? -1 : b.index;
                if (a_index < b_index) {
                    return -1;
                } else if (a_index > b_index) {
                    return 1;
                } else if (a_index === -1) {
                    //a_index === b_index === -1
                    //sort local nodes by local index
                    if (a.localIdx < b.localIdx) {
                        return -1;
                    } else if (a.localIdx > b.localIdx) {
                        return 1;
                    } else {
                        return 0;
                    }
                } else {
                    return 0;
                }
            });
        }
    }

    /* public getForExport(url: string): Observable<Object> {
        this._loaderSubject.next(true);
        return this._authhttp.get(url).map(res => {
            this._loaderSubject.next(false);
            return res;
        });
    } */

    public setLoader(value: boolean) {
        this._loaderSubject.next(value);
    }

    public setDataWithUrl(url:any, data:any, operation: any): Observable<Object>{
        this._loaderSubject.next(true);
        let obsRequest;

        if (operation === "#" || operation === "" || operation === undefined) {
            obsRequest =  this._apiService.putObject(url, data);
        }
        else if (operation === "+") {
            obsRequest =  this._apiService.postObject(url, data);
        }

        else if (operation === "-") {
            obsRequest =  this._apiService.deleteObject(url);
        }
        /* if (modify === true) {
            obsRequest =  this._apiService.putObject(url, data);
        } else {
            obsRequest = this._apiService.postObject(url, data);
        }
        */
        return obsRequest.pipe(
            catchError((error) => {
                // preserve previous behavior: record loader off and capture error details where possible
                this.errorCode = error;
                try {
                    this.errorMsg = error.json().message;
                    this.errorDetailMsg = error.json().details[0].message;
                    this.errorDetailTarget = error.json().details[0].target;
                } catch (e) {
                    // ignore parsing errors
                }
                this._loaderSubject.next(false);
                // rethrow so callers can handle error if they expect it
                return observableThrowError(error);
            })
        );
    }

    public getDataWithUrl(url:any): Observable<Object>{
        this._loaderSubject.next(true);
        setTimeout(() => { }, 4000);
        let obsRequest;
        obsRequest = this._apiService.retrieveObject(url);
        /* setTimeout(() => { }, 1000); */
        return obsRequest.pipe(
            catchError((error) => {
                this._loaderSubject.next(false);
                return observableOf([]);
            })
        );
    }

    public setFreeNumber(index: number): Observable<Object> {
                return this._apiService.retriveFreeNumberRange().pipe(
                map(response => response[index]["Number"]),
                   catchError(e => {
                        return observableOf(null);
                    })
                );
        }

    public setData(node: INode, objData: Object): Observable<Object> {
        this._loaderSubject.next(true);
        const onRejected = (err) => {
            this._loaderSubject.next(false);
            this.handleError(node, err);
            return observableThrowError(err);
        };

        if (node.structure) {
            const nodeIdAttrName = node.structure['id'];

            //check that id of a local node is not empty
            if (node.isLocal && node.name === '') {
                this._loaderSubject.next(false);
                let translateNodeIdAttrName = nodeIdAttrName;
                this._translate.get(translateNodeIdAttrName).subscribe((translated) => translateNodeIdAttrName = translated);
                let emptyLabel = 'empty';
                this._translate.get(emptyLabel).subscribe((translated) => emptyLabel = translated);
                const err = new RangeError(translateNodeIdAttrName + ': ' + emptyLabel);
                return observableThrowError(err);
            }

            //remove id from list of modified attributes (to be compliant with OXE backend REST api)
            if (nodeIdAttrName && Object.keys(objData) && Object.keys(objData).indexOf(nodeIdAttrName) !== -1) {
                this._logger.debug('OxeModelService', 'setData: remove node attribute (id): ' + nodeIdAttrName);
                delete objData[nodeIdAttrName];
            }
        }

        let obsRequest = node.isLocal ? this._apiService.postObject(node.getPath(), objData).pipe(
            map((resp) => {
                //If the node just submitted is a local node, copy id local value to value (before calling clearLocalChanges() )
                // so that the name of the node remains correct after clearing attribute localvalues
                node.copyIdlocalToValue();
                node.isLocal = false;
                return resp;
            })) : this._apiService.putObject(node.getPath(), objData);
        if (node.parent.name === 'ACD2_Pilot') {
            node.attributes[0].value = objData['Pilot_Numan'];
        } else if (node.parent.name === 'ACD2_PilotStat') {
            node.attributes[0].value = objData['PilotStat_Numan'];
        }

        return obsRequest.pipe(
            mergeMap(() => this.retrieveData(node)),
            map(() => {
                node.clearLocalChanges();
                node.removeAttributesMarkedToDelete();
                this.filterAndSortDisplayedSubNode(node.parent);
                this.selectNode(node);
                this._loaderSubject.next(false);
                return {};
            }),
            catchError(onRejected)
        );
    }

    public deleteData(node: INode): Observable<Object> {
        if (node.isLocal) {
            node.parent.subNodes.splice(node.parent.subNodes.indexOf(node), 1);
            node.parent.subNodesInitialLength--;
            return observableOf(node);
        } else {
            const onRejected = (err) => {
                this.handleError(node, err);
                return observableThrowError(err);
            };

            return this._apiService.deleteObject(node.getPath()).pipe(
                //remove node from parent subnodes
                map(() => {
                    node.parent.subNodes.splice(node.parent.subNodes.indexOf(node), 1);
                    node.parent.subNodesInitialLength--;
                    return observableOf(node);
                }),
                catchError(onRejected)
            );
        }
    }

    public action(node: INode, actionData: Object): Observable<Object> {

        const onRejected = (err) => {
            this.handleError(node, err);
            return observableThrowError(err);
        };

        // Send action request and don't retrieve any data after response
        return this._apiService.action(node.getPath(), actionData).pipe(
            catchError(onRejected)
        );
    }

    public selectNode(node: INode) {
        if (node.isOneInstance && node.isObjectNode && node.parent) {
            this._selectedNodeSubject.next(node.parent);
        } else {
            this._selectedNodeSubject.next(node);
        }
    }

    public helpNode(node: INode) {
        this._helpNodeSubject.next(node);
    }

    private handleError(node: INode, error: any) {
        switch (this._apiService.getErrorCode(error)) {
            case 'CMISERR_0':
            case 'CMISERR_1':
                this._logger.info('OxeModelService', 'Error: ' + error);
                if (node.parent !== undefined && !node.isOneInstance && !node.isLocal) {
                    // element not found: remove entry and change selection
                    let elementIndex = node.parent.subNodes.indexOf(node, 0);
                    node.parent.subNodes.splice(elementIndex, 1);
                    this.selectNode(node.parent);
                    this.filterAndSortDisplayedSubNode(node.parent);
                }
                break;
            default:
                this._logger.error('OxeModelService', 'Error: ' + error);
                break;
        }
    }

    public isSomethingModified(): boolean {
        return this._rootNode.isModified;
    }

    public downloadLogs(): void {
        this._loaderSubject.next(true);

        this._indexedDB.retrieveLogsStoreIfCreated()
            .then((logStr: string) => {
                this._logger.debug('OxeModelService', 'Logs retrieved from the indexed database');
                this._loaderSubject.next(false);

                //create a Blob object
                let blobObject = new Blob([logStr], {
                    type: 'text/plain'
                });

                let fileName = 'OXE-WBM-' + moment().format('DD-MM-YYYY--H-mm-ss') + '.log';
                let url = window.URL.createObjectURL(blobObject);

                let e = document.createElement('a');
                e.setAttribute('href', url);
                e.setAttribute('download', fileName);
                document.body.appendChild(e);
                e.click();
                document.body.removeChild(e);
            })
            .catch(() => {
                this._loaderSubject.next(false);
            });
    }

    public filterAndSortDisplayedSubNode(node: INode) {
        if (node && node.isClassNode) {
            this._logger.debug('OxeModelService', 'filterAndSortDisplayedSubNode... nodes:' + (node.subNodes ? node.subNodes.length : 0));
            this.initAndFilterDisplayedSubNodes(node);
            this.sortDisplayedSubNodes(node);
            this._logger.debug('OxeModelService', 'filterAndSortDisplayedSubNode... done. => nodes:' + (node.subNodes ? node.subNodes.length : 0));
        }
    }

    public sortDisplayedSubNodes(node: INode): void {
        if (!node.subNodes) {
            return;
        }
        // temporary array holds objects with position and sort-value
        let sortValues = node.subNodes.map((subNode, i) => {
            let elem = { _index: i, _isLocal: subNode.isLocal, _serverIndex: (subNode.index === undefined) ? -1 : subNode.index };
            //the _ is used in property names to avoid conflicts with sort attribute names
            if (subNode.isLocal) {
                elem['_localIdx'] = subNode.localIdx;
            }
            //add attribute sort-value property in element :  the property is the sort attribute name
            for (let attributeName in node.sortOrder) {
                if (node.sortOrder.hasOwnProperty(attributeName)) {
                    const attribute = subNode.getAttributeByName(attributeName);
                    //console.log("here ---->"+attribute);
                    if (!attribute) {
                        continue;
                    }
                    if (attribute.isEnumerated) {
                        this._translate.get(attribute.getStringValue()).subscribe(translatedStr => elem[attributeName] = translatedStr);
                    } else {
                        elem[attributeName] = attribute.getStringValue();
                    }
                }
            }
            return elem;
        });

        // sorting the mapped array containing the reduced values
        sortValues.sort((a, b) => {
            if (a._isLocal !== b._isLocal) {
                return a._isLocal ? -1 : 1;
            } else if (a._isLocal) {
                return NodeSort.nodeSortOnUnsavedIndex(a['_localIdx'], b['_localIdx']);
            } else {
                let comparisonResult = (a._serverIndex > b._serverIndex) ? 1 : (a._serverIndex < b._serverIndex) ? -1 : 0;
                let attributeComparisonResult = 0;
                for (let index = 0; index < node.getInitialAttributesToRetrieve().length; ++index) {
                    //the loop is done on getInitialAttributesToRetrieve array by index in order to sort with the right order between sorts if several sort attributes are set
                    const attribute = node.getInitialAttributesToRetrieve()[index];
                    if (node.sortOrder.hasOwnProperty(attribute.name)) {
                        let order = node.sortOrder[attribute.name];
                        // compare attributes described by the current sort criteria
                        attributeComparisonResult = NodeSort.nodeSortOnAttributes(a[attribute.name], b[attribute.name]);
                        // inverse the result in case of a descending sort
                        attributeComparisonResult = order === 'desc' ? attributeComparisonResult * -1 : attributeComparisonResult;

                        if (order !== 'none' && attributeComparisonResult !== 0) {
                            // an order has been found!
                            break;
                        }
                    }
                };
                //if the attributeComparisonResult is 0, we return the comparisonResult based on serverIndex
                if (attributeComparisonResult !== 0) {
                    comparisonResult = attributeComparisonResult;
                }
                return comparisonResult;
            }
        });

        node.subNodes = sortValues.map((el) => node.subNodes[el._index]);
    }
}
